import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { S, Field, Select, Modal, SearchSelect, itemOptions, fmtQty, fmtMoney, fmtDate, today } from './StoresShared';
import { statusPill, useList } from './StoresPurchase';

// Stores module — Phase 2b stock movement screens: Indents, Issue & Return,
// Transfers, Wastage & Adjustments, Physical Count.
// API: backend/v1/routes/storesMovements.js. Stock never goes below zero; a
// Master / Super Admin can authorise a shortfall with a reason (override).

const COST_REF_TYPES = [['OUTLET', 'Outlet / kitchen'], ['DEPARTMENT', 'Department'], ['ROOM', 'Room'], ['WORK_ORDER', 'Work order'], ['EVENT', 'Event / banquet'], ['STAFF', 'Staff'], ['OTHER', 'Other']];
const WASTAGE_REASONS = [['EXPIRED', 'Expired'], ['DAMAGED', 'Damaged'], ['SPOILED', 'Spoiled'], ['OVER_PRODUCTION', 'Over-production'], ['PILFERAGE', 'Pilferage / theft'], ['OTHER', 'Other']];
const ADJUST_REASONS = [['DATA_ENTRY', 'Data entry correction'], ['FOUND', 'Found stock'], ['MISSING', 'Missing stock'], ['UNIT_CORRECTION', 'Unit correction'], ['OTHER', 'Other']];
const RETURN_REASONS = [['UNUSED', 'Not used'], ['EXCESS', 'Issued in excess'], ['WRONG_ITEM', 'Wrong item'], ['OTHER', 'Other']];

const n = (v) => Number(v) || 0;
const r4 = (v) => Math.round(n(v) * 10000) / 10000;
const key = () => Math.random().toString(36).slice(2);
const small = { fontSize: '0.72rem', color: '#94a3b8' };
const can = (boot, ...roles) => boot.me.is_hotel_admin || roles.some((r) => boot.me.roles.includes(r));
const label = (list, v) => (list.find(([k]) => k === v) || [v, v])[1];
const unitsOf = (it) => (it ? [it.base_uom, ...(it.uoms || []).map((u) => u.uom_code)] : []);
const factorOf = (it, uom) => {
  if (!it || !uom || uom === it.base_uom) return 1;
  const u = (it.uoms || []).find((x) => x.uom_code === uom);
  return u ? n(u.factor_to_base) : 1;
};
const issueUnit = (it) => (it?.uoms || []).find((u) => u.is_issue_uom)?.uom_code || it?.base_uom || '';
const blankLine = (extra = {}) => ({ key: key(), item_id: '', uom_code: '', qty: '', remarks: '', ...extra });

function useItems(api) {
  const [items, setItems] = useState([]);
  useEffect(() => { api('/items?active=1').then(setItems).catch(() => setItems([])); }, [api]);
  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  return { items, byId };
}

// On-hand (base unit) per item for one store.
function useOnHand(api, storeId) {
  const [map, setMap] = useState({});
  const load = useCallback(() => {
    if (!storeId) { setMap({}); return; }
    api(`/stock?store_id=${storeId}&include_zero=1`)
      .then((rows) => setMap(Object.fromEntries(rows.map((r) => [r.item_id, n(r.on_hand_qty)]))))
      .catch(() => setMap({}));
  }, [api, storeId]);
  useEffect(() => { load(); }, [load]);
  return [map, load];
}

// Posts; when stock is short and the user is a Master / Super Admin, offers
// an override with a reason and posts again.
async function postWithOverride(api, path, body, boot) {
  let lines = body.lines;
  for (let attempt = 0; attempt <= lines.length; attempt += 1) {
    try {
      return await api(path, { method: 'POST', body: { ...body, lines } });
    } catch (e) {
      const itemId = e.details?.item_id;
      const short = e.details && e.details.available != null;
      if (!itemId || !short || !boot.me.is_platform_admin) throw e;
      const reason = window.prompt(`${e.message}.\n\nAs Master / Super Admin you can authorise this shortfall. It is logged in the override register.\nReason (at least 5 characters), or Cancel:`);
      if (!reason) throw e;
      lines = lines.map((l) => (l.item_id === itemId ? { ...l, override_reason: reason } : l));
    }
  }
  throw new Error('Could not post');
}

function Available({ it, onHand, uom }) {
  if (!it) return '—';
  const base = n(onHand[it.id]);
  const f = factorOf(it, uom);
  return (
    <span style={{ color: base > 0 ? '#334155' : '#b91c1c', whiteSpace: 'nowrap' }}>
      {fmtQty(r4(base / f))} {uom || it.base_uom}
    </span>
  );
}

function CostRefFields({ form, setForm, disabled }) {
  return (
    <>
      <Field label="Issued to">
        <Select style={S.inp} value={form.cost_ref_type} disabled={disabled} onChange={(e) => setForm({ ...form, cost_ref_type: e.target.value })}>
          {COST_REF_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
      </Field>
      <Field label="Name / number *"><input style={{ ...S.inp, width: 180 }} value={form.cost_ref_id} disabled={disabled} placeholder="e.g. Main Kitchen, Room 204" onChange={(e) => setForm({ ...form, cost_ref_id: e.target.value })} /></Field>
    </>
  );
}

// Generic list of item lines (item / unit / qty + extra columns).
function LineTable({ form, setForm, items, byId, onHand, extraHead, extraCells, newLine, fixedItems }) {
  const setLine = (k, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === k ? { ...l, ...patch } : l)) }));
  return (
    <>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Item</th><th style={S.th}>Unit</th><th style={{ ...S.th, ...S.num }}>In stock</th>
            {extraHead}
            <th style={{ ...S.th, ...S.num }}>Qty</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {form.lines.map((l) => {
              const it = byId[l.item_id];
              return (
                <tr key={l.key}>
                  <td style={S.td}>
                    {fixedItems ? <><div style={{ fontWeight: 600 }}>{it?.item_name || '…'}</div><div style={small}>{it?.item_code}</div></> : (
                      <SearchSelect style={{ minWidth: 230 }} value={l.item_id} options={itemOptions(items)} onChange={(v) => setLine(l.key, { item_id: v, uom_code: issueUnit(byId[v]) })} />
                    )}
                    {it && l.uom_code && l.uom_code !== it.base_uom && <div style={small}>1 {l.uom_code} = {fmtQty(factorOf(it, l.uom_code))} {it.base_uom}</div>}
                  </td>
                  <td style={S.td}>
                    {fixedItems ? l.uom_code : (
                      <Select style={{ ...S.inp, width: 85 }} value={l.uom_code} disabled={!it} onChange={(e) => setLine(l.key, { uom_code: e.target.value })}>
                        {unitsOf(it).map((u) => <option key={u} value={u}>{u}</option>)}
                      </Select>
                    )}
                  </td>
                  <td style={{ ...S.td, ...S.num }}><Available it={it} onHand={onHand} uom={l.uom_code} /></td>
                  {extraCells && extraCells(l, it, (patch) => setLine(l.key, patch))}
                  <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 90, textAlign: 'right' }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                  <td style={S.td}>
                    <button type="button" className="btn-muted btn-sm" aria-label="Remove line"
                      onClick={() => setForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((x) => x.key !== l.key) : (fixedItems ? f.lines : [newLine()]) }))}>✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!fixedItems && <button type="button" className="btn-muted btn-sm" style={{ marginTop: '0.6rem' }} onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, newLine()] }))}>+ Add line</button>}
    </>
  );
}

// ── Movement document view ───────────────────────────────────────────────
const DOC_TITLES = { INDENT: 'Indent', ISSUE: 'Issue', RETURN: 'Return', TRANSFER: 'Transfer', WASTAGE: 'Wastage', ADJUST: 'Adjustment', COUNT: 'Physical count', LINEN: 'Linen', CONSUMPTION: 'Room consumption' };

export function MoveDocView({ doc, onClose, children }) {
  const t = doc.doc_type;
  return (
    <Modal title={<>{DOC_TITLES[t] || t} {doc.doc_no} {statusPill(doc.status)}</>} onClose={onClose} width={980}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.5rem 1rem', fontSize: '0.86rem', marginBottom: '0.8rem' }}>
        <div><span style={S.lbl}>{t === 'TRANSFER' ? 'From' : t === 'RETURN' ? 'Into store' : 'Store'}</span>{doc.from_store_name || doc.to_store_name}<div style={small}>{doc.department_name}</div></div>
        {t === 'TRANSFER' && <div><span style={S.lbl}>To</span>{doc.to_store_name}</div>}
        <div><span style={S.lbl}>Date</span>{fmtDate(doc.doc_date)}</div>
        {doc.cost_ref_id && <div><span style={S.lbl}>{t === 'RETURN' ? 'Returned by' : t === 'INDENT' ? 'For' : 'Issued to'}</span>{doc.cost_ref_id}<div style={small}>{label(COST_REF_TYPES, doc.cost_ref_type)}</div></div>}
        {doc.party_name && <div><span style={S.lbl}>{t === 'INDENT' ? 'Requested by' : 'Received / handed by'}</span>{doc.party_name}</div>}
        {doc.ref_doc_no && <div><span style={S.lbl}>Against</span>{doc.ref_doc_no}</div>}
        <div><span style={S.lbl}>By</span>{doc.posted_by || doc.created_by || '—'}{doc.approved_by ? <div style={small}>approved by {doc.approved_by}</div> : null}</div>
      </div>
      {doc.cancel_reason && <div style={S.err}>{doc.status} by {doc.cancelled_by}: {doc.cancel_reason}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>#</th><th style={S.th}>Item</th>
            {t === 'INDENT' && <><th style={{ ...S.th, ...S.num }}>Requested</th><th style={{ ...S.th, ...S.num }}>Issued</th><th style={{ ...S.th, ...S.num }}>Pending</th></>}
            {t === 'COUNT' && <><th style={{ ...S.th, ...S.num }}>System</th><th style={{ ...S.th, ...S.num }}>Counted</th><th style={{ ...S.th, ...S.num }}>Variance</th></>}
            {!['INDENT', 'COUNT'].includes(t) && <th style={{ ...S.th, ...S.num }}>Qty</th>}
            {t === 'ISSUE' && <th style={{ ...S.th, ...S.num }}>Returned</th>}
            {['WASTAGE', 'ADJUST', 'RETURN'].includes(t) && <th style={S.th}>Reason</th>}
            {t !== 'INDENT' && <><th style={{ ...S.th, ...S.num }}>Cost / unit</th><th style={{ ...S.th, ...S.num }}>Value</th></>}
          </tr></thead>
          <tbody>
            {doc.lines.map((l) => (
              <tr key={l.id}>
                <td style={S.td}>{l.line_no}</td>
                <td style={S.td}>{l.item_name}<div style={small}>{l.item_code}{l.batch_no ? ` · batch ${l.batch_no}` : ''}{l.remarks ? ` · ${l.remarks}` : ''}</div></td>
                {t === 'INDENT' && <>
                  <td style={{ ...S.td, ...S.num }}>{fmtQty(l.qty)} {l.uom_code}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtQty(l.issued_qty)}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtQty(Math.max(0, n(l.qty) - n(l.issued_qty)))}</td>
                </>}
                {t === 'COUNT' && <>
                  <td style={{ ...S.td, ...S.num }}>{fmtQty(l.system_qty)} {l.uom_code}</td>
                  <td style={{ ...S.td, ...S.num }}>{l.counted_qty != null ? fmtQty(l.counted_qty) : '—'}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 700, color: n(l.qty) < 0 ? '#b91c1c' : n(l.qty) > 0 ? '#15803d' : undefined }}>{doc.status === 'POSTED' ? fmtQty(l.qty) : '—'}</td>
                </>}
                {!['INDENT', 'COUNT'].includes(t) && <td style={{ ...S.td, ...S.num, color: n(l.qty) < 0 ? '#b91c1c' : undefined }}>{n(l.qty) > 0 && t === 'ADJUST' ? '+' : ''}{fmtQty(l.qty)} {l.uom_code}</td>}
                {t === 'ISSUE' && <td style={{ ...S.td, ...S.num }}>{n(l.returned_qty) ? fmtQty(l.returned_qty) : '—'}</td>}
                {['WASTAGE', 'ADJUST', 'RETURN'].includes(t) && <td style={S.td}>{l.reason ? label([...WASTAGE_REASONS, ...ADJUST_REASONS, ...RETURN_REASONS], l.reason) : '—'}</td>}
                {t !== 'INDENT' && <>
                  <td style={{ ...S.td, ...S.num }}>{fmtMoney(l.rate)}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtMoney(l.amount)}</td>
                </>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginTop: '0.7rem', fontSize: '0.86rem' }}>
        <div>
          {doc.remarks && <div><strong>Remarks:</strong> {doc.remarks}</div>}
          {doc.linked_docs?.length > 0 && <div><strong>Linked:</strong> {doc.linked_docs.map((x) => <span key={x.id} style={{ marginRight: 10 }}>{x.doc_no} {statusPill(x.status)}</span>)}</div>}
        </div>
        {t !== 'INDENT' && <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{t === 'COUNT' || t === 'ADJUST' ? 'Net value' : 'Value'} {fmtMoney(doc.grand_total)}</strong>}
      </div>
      {children && <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '0.9rem', borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>{children}</div>}
    </Modal>
  );
}

function DocList({ rows, loading, empty, columns, onView }) {
  return (
    <div style={S.tableWrap}>
      <table style={S.table}>
        <thead><tr>{columns.map(([h, , num]) => <th key={h} style={num ? { ...S.th, ...S.num } : S.th}>{h}</th>)}<th style={S.th} /></tr></thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.id}>
              {columns.map(([h, cell, num]) => <td key={h} style={num ? { ...S.td, ...S.num } : S.td}>{cell(d)}</td>)}
              <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => onView(d.id)}>View</button></td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={columns.length + 1} style={S.empty}>{loading ? 'Loading…' : empty}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
const colNo = ['No.', (d) => <strong style={{ whiteSpace: 'nowrap' }}>{d.doc_no}</strong>];
const colDate = ['Date', (d) => fmtDate(d.doc_date)];
const colValue = ['Value', (d) => fmtMoney(d.grand_total), true];
const colStatus = ['Status', (d) => statusPill(d.status)];

// ── Indents ──────────────────────────────────────────────────────────────
export function Indents({ api, boot }) {
  const { items, byId } = useItems(api);
  const [status, setStatus] = useState('SUBMITTED,APPROVED,PARTIAL');
  const { rows, err: listErr, loading, load } = useList(api, `/docs?doc_type=INDENT${status ? `&status=${status}` : ''}`);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [issueFrom, setIssueFrom] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const stores = boot.stores.filter((s) => s.is_active);
  const store = form && boot.stores.find((s) => s.id === form.store_id);
  const [onHand] = useOnHand(api, form?.store_id);
  const canApprove = can(boot, 'DEPT_HEAD', 'STORE_MANAGER');
  const canIssue = can(boot, 'STOREKEEPER', 'STORE_MANAGER');

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const d = await api('/indents', { method: 'POST', body: { ...form, lines: form.lines.filter((l) => l.item_id && n(l.qty) > 0) } });
      setMsg(`${d.doc_no} ${d.status === 'APPROVED' ? 'raised — the store can issue it now' : 'sent for approval'}.`);
      setForm(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const open = (id) => api(`/docs/${id}`).then((d) => { setErr(''); setView(d); }).catch((e) => setErr(e.message));
  const act = async (path, body, done) => {
    setBusy(true); setErr('');
    try { await api(path, { method: 'POST', body }); setMsg(done); setView(null); load(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (issueFrom) {
    return <IssueForm api={api} boot={boot} indent={issueFrom} onCancel={() => setIssueFrom(null)} onDone={(d) => { setIssueFrom(null); setMsg(`Issued ${d.doc_no} — ${fmtMoney(d.grand_total)}.`); load(); }} />;
  }

  return (
    <>
      {(err || listErr) && !view && <div style={S.err}>{err || listErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {!form && (
        <div style={S.bar}>
          <button type="button" className="btn-primary" disabled={!stores.length} onClick={() => { setErr(''); setMsg(''); setForm({ store_id: stores[0]?.id || '', doc_date: today(), cost_ref_type: 'OUTLET', cost_ref_id: '', party_name: '', remarks: '', lines: [blankLine()] }); }}>+ New indent</button>
          <Field label="Show">
            <Select style={S.inp} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="SUBMITTED,APPROVED,PARTIAL">Open</option>
              <option value="SUBMITTED">Waiting for approval</option>
              <option value="ISSUED,CLOSED">Issued / closed</option>
              <option value="REJECTED,CANCELLED">Rejected / cancelled</option>
              <option value="">All</option>
            </Select>
          </Field>
        </div>
      )}
      {form && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#1e3a5f' }}>New indent (store requisition)</h3>
          {err && <div style={S.err}>{err}</div>}
          <div style={S.bar}>
            <Field label="From store">
              <Select style={{ ...S.inp, minWidth: 190 }} value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value, lines: [blankLine()] })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <CostRefFields form={form} setForm={setForm} />
            <Field label="Requested by"><input style={{ ...S.inp, width: 150 }} value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} placeholder="Your name" /></Field>
            <Field label="Date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
          </div>
          <LineTable form={form} setForm={setForm} items={store ? items.filter((i) => i.department_id === store.department_id) : []} byId={byId} onHand={onHand} newLine={() => blankLine()} />
          <Field label="Remarks" style={{ marginTop: '0.75rem' }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={submit} disabled={busy || !form.cost_ref_id.trim()}>{busy ? 'Sending…' : 'Raise indent'}</button>
          </div>
        </div>
      )}
      <DocList rows={rows} loading={loading} empty="No indents." onView={open} columns={[
        colNo, colDate, ['Store', (d) => d.from_store_name], ['For', (d) => <>{d.cost_ref_id}<div style={small}>{label(COST_REF_TYPES, d.cost_ref_type)}</div></>],
        ['Requested by', (d) => d.party_name || d.created_by], ['Lines', (d) => d.line_count, true], colStatus,
      ]} />
      {view && (
        <MoveDocView doc={view} onClose={() => setView(null)}>
          {err && <div style={{ ...S.err, flexBasis: '100%' }}>{err}</div>}
          {['SUBMITTED', 'APPROVED', 'PARTIAL'].includes(view.status) && (
            <button type="button" className="btn-muted" disabled={busy} onClick={() => {
              const reason = window.prompt(view.status === 'PARTIAL' ? 'Close this indent (the rest will not be issued). Reason?' : 'Reason for rejecting / cancelling this indent?');
              if (reason) act(`/indents/${view.id}/cancel`, { reason }, `${view.doc_no} ${view.status === 'PARTIAL' ? 'closed' : 'cancelled'}.`);
            }}>{view.status === 'PARTIAL' ? 'Close' : 'Reject / cancel'}</button>
          )}
          {view.status === 'SUBMITTED' && canApprove && <button type="button" className="btn-primary" disabled={busy} onClick={() => act(`/indents/${view.id}/approve`, {}, `${view.doc_no} approved.`)}>Approve</button>}
          {['APPROVED', 'PARTIAL'].includes(view.status) && canIssue && <button type="button" className="btn-primary" onClick={() => { setIssueFrom(view); setView(null); setMsg(''); }}>Issue items</button>}
        </MoveDocView>
      )}
    </>
  );
}

// Pick an open maintenance work order as the "issued to"; its PM kit (if
// any) can fill the lines in one go.
function WorkOrderPicker({ api, form, setForm }) {
  const [wos, setWos] = useState([]);
  const [err, setErr] = useState('');
  useEffect(() => { api('/work-orders/open').then(setWos).catch(() => setWos([])); }, [api]);
  const wo = wos.find((w) => w.work_order_no === form.cost_ref_id);
  const loadKit = async () => {
    setErr('');
    try {
      const kits = await api('/pm-kits');
      const kit = kits.find((k) => Number(k.schedule_id) === Number(wo.pm_schedule_id));
      if (!kit?.kit?.length) { setErr('No kit is set up for this PM schedule.'); return; }
      setForm((f) => ({ ...f, lines: kit.kit.map((k) => ({ ...blankLine(), item_id: k.item_id, uom_code: k.uom_code, qty: String(n(k.qty)) })) }));
    } catch (e) { setErr(e.message); }
  };
  return (
    <>
      <Field label="Work order *">
        <Select style={{ ...S.inp, minWidth: 260 }} value={form.cost_ref_id} onChange={(e) => setForm({ ...form, cost_ref_id: e.target.value })}>
          <option value="">{wos.length ? 'Choose…' : 'No open work orders'}</option>
          {wos.map((w) => <option key={w.work_order_id} value={w.work_order_no}>{w.work_order_no} · {(w.room_number ? `Room ${w.room_number} · ` : '') + String(w.description || w.category).slice(0, 40)}</option>)}
        </Select>
      </Field>
      {wo && n(wo.kit_lines) > 0 && <button type="button" className="btn-muted" onClick={loadKit}>Load PM kit ({wo.kit_lines})</button>}
      {err && <span style={{ ...S.sub, color: '#b91c1c', paddingBottom: 6 }}>{err}</span>}
    </>
  );
}

// ── Issue form (direct, or against an indent) ────────────────────────────
function IssueForm({ api, boot, indent, onCancel, onDone }) {
  const { items, byId } = useItems(api);
  const stores = boot.stores.filter((s) => s.is_active);
  const [openIndents, setOpenIndents] = useState([]);
  const [form, setForm] = useState(() => ({
    store_id: indent?.from_store_id || stores[0]?.id || '', indent_id: '', doc_date: today(),
    cost_ref_type: 'OUTLET', cost_ref_id: '', party_name: '', remarks: '', lines: [blankLine()],
  }));
  const [onHand] = useOnHand(api, form.store_id);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const store = boot.stores.find((s) => s.id === form.store_id);

  const fromIndent = useCallback((d) => {
    setForm((f) => ({
      ...f, indent_id: d.id, store_id: d.from_store_id, cost_ref_type: d.cost_ref_type || 'DEPARTMENT', cost_ref_id: d.cost_ref_id || '', party_name: d.party_name || '',
      lines: d.lines.filter((l) => n(l.qty) - n(l.issued_qty) > 0.000001).map((l) => ({
        ...blankLine(), indent_line_id: l.id, item_id: l.item_id, uom_code: l.uom_code, requested: n(l.qty), pending: r4(n(l.qty) - n(l.issued_qty)), qty: String(r4(n(l.qty) - n(l.issued_qty))),
      })),
    }));
  }, []);
  useEffect(() => { if (indent) fromIndent(indent); }, [indent, fromIndent]);
  useEffect(() => {
    if (indent || !form.store_id) return;
    api(`/indents/open?store_id=${form.store_id}`).then(setOpenIndents).catch(() => setOpenIndents([]));
  }, [api, indent, form.store_id]);

  const pickIndent = async (id) => {
    if (!id) { setForm((f) => ({ ...f, indent_id: '', lines: [blankLine()] })); return; }
    try { fromIndent(await api(`/docs/${id}`)); } catch (e) { setErr(e.message); }
  };

  const post = async () => {
    setBusy(true); setErr('');
    try {
      const lines = form.lines.filter((l) => l.item_id && n(l.qty) > 0).map((l) => ({ indent_line_id: l.indent_line_id, item_id: l.item_id, uom_code: l.uom_code, qty: l.qty, remarks: l.remarks }));
      if (!lines.length) throw new Error('Enter a quantity to issue');
      const d = await postWithOverride(api, '/issues', { ...form, indent_id: form.indent_id || null, lines }, boot);
      onDone(d);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={S.card}>
      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#1e3a5f' }}>Issue items{indent ? ` against ${indent.doc_no}` : ''}</h3>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.bar}>
        <Field label="From store">
          <Select style={{ ...S.inp, minWidth: 190 }} value={form.store_id} disabled={Boolean(form.indent_id)} onChange={(e) => setForm({ ...form, store_id: e.target.value, lines: [blankLine()] })}>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </Select>
        </Field>
        {!indent && (
          <Field label="Against indent">
            <Select style={{ ...S.inp, minWidth: 210 }} value={form.indent_id} onChange={(e) => pickIndent(e.target.value)}>
              <option value="">{openIndents.length ? 'No indent — direct issue' : 'No open indents'}</option>
              {openIndents.map((d) => <option key={d.id} value={d.id}>{d.doc_no} · {d.cost_ref_id} · {fmtDate(d.doc_date)}</option>)}
            </Select>
          </Field>
        )}
        {form.cost_ref_type === 'WORK_ORDER' && !form.indent_id ? (
          <>
            <Field label="Issued to">
              <Select style={S.inp} value={form.cost_ref_type} onChange={(e) => setForm({ ...form, cost_ref_type: e.target.value, cost_ref_id: '' })}>
                {COST_REF_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </Field>
            <WorkOrderPicker api={api} form={form} setForm={setForm} />
          </>
        ) : <CostRefFields form={form} setForm={setForm} disabled={Boolean(form.indent_id)} />}
        <Field label="Received by"><input style={{ ...S.inp, width: 140 }} value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} /></Field>
        <Field label="Date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
      </div>
      <LineTable form={form} setForm={setForm} items={store ? items.filter((i) => i.department_id === store.department_id) : []} byId={byId} onHand={onHand}
        newLine={() => blankLine()} fixedItems={Boolean(form.indent_id)}
        extraHead={form.indent_id ? <th style={{ ...S.th, ...S.num }}>Pending</th> : null}
        extraCells={form.indent_id ? (l) => <td style={{ ...S.td, ...S.num }}>{fmtQty(l.pending)}</td> : null} />
      <Field label="Remarks" style={{ marginTop: '0.75rem' }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
      <p style={{ ...S.sub }}>Stock is taken oldest first (earliest expiry first for chilled / frozen items) and valued at its cost.</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button type="button" className="btn-muted" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={post} disabled={busy || !form.cost_ref_id.trim()}>{busy ? 'Issuing…' : 'Post issue'}</button>
      </div>
    </div>
  );
}

function ReturnForm({ api, issue, onCancel, onDone }) {
  const [form, setForm] = useState(() => ({
    doc_date: today(), party_name: '', remarks: '',
    lines: issue.lines.map((l) => ({ key: key(), issue_line_id: l.id, item_name: l.item_name, item_code: l.item_code, uom_code: l.uom_code, left: r4(n(l.qty) - n(l.returned_qty)), qty: '', reason: 'UNUSED' })),
  }));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const setLine = (k, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === k ? { ...l, ...patch } : l)) }));
  const post = async () => {
    setBusy(true); setErr('');
    try {
      const lines = form.lines.filter((l) => n(l.qty) > 0);
      if (!lines.length) throw new Error('Enter the quantity coming back');
      onDone(await api('/returns', { method: 'POST', body: { ...form, issue_id: issue.id, lines } }));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Return to ${issue.from_store_name} — from ${issue.doc_no}`} onClose={onCancel} width={820}>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.bar}>
        <Field label="Date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
        <Field label="Returned by"><input style={{ ...S.inp, width: 170 }} value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} /></Field>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Item</th><th style={{ ...S.th, ...S.num }}>Can return</th><th style={{ ...S.th, ...S.num }}>Qty back</th><th style={S.th}>Reason</th></tr></thead>
          <tbody>
            {form.lines.map((l) => (
              <tr key={l.key}>
                <td style={S.td}>{l.item_name}<div style={small}>{l.item_code}</div></td>
                <td style={{ ...S.td, ...S.num }}>{fmtQty(l.left)} {l.uom_code}</td>
                <td style={S.td}><input type="number" min="0" max={l.left} step="any" disabled={l.left <= 0} style={{ ...S.inp, width: 90, textAlign: 'right', marginLeft: 'auto', display: 'block' }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                <td style={S.td}><Select style={S.inp} value={l.reason} onChange={(e) => setLine(l.key, { reason: e.target.value })}>{RETURN_REASONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Field label="Remarks" style={{ marginTop: '0.75rem' }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
      <p style={S.sub}>Returned stock goes back at the cost it was issued at.</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button type="button" className="btn-muted" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={post} disabled={busy}>{busy ? 'Posting…' : 'Post return'}</button>
      </div>
    </Modal>
  );
}

// ── Issue & Return ───────────────────────────────────────────────────────
export function IssuesReturns({ api, boot }) {
  const [tab, setTab] = useState('issues');
  const issues = useList(api, tab === 'issues' ? '/docs?doc_type=ISSUE' : null);
  const returns = useList(api, tab === 'returns' ? '/docs?doc_type=RETURN' : null);
  const [issuing, setIssuing] = useState(false);
  const [view, setView] = useState(null);
  const [returning, setReturning] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const canMove = can(boot, 'STOREKEEPER', 'STORE_MANAGER');
  const open = (id) => api(`/docs/${id}`).then((d) => { setErr(''); setView(d); }).catch((e) => setErr(e.message));

  if (issuing) {
    return <IssueForm api={api} boot={boot} indent={null} onCancel={() => setIssuing(false)} onDone={(d) => { setIssuing(false); setMsg(`Issued ${d.doc_no} — ${fmtMoney(d.grand_total)}.`); issues.load(); }} />;
  }
  return (
    <>
      {(err || issues.err || returns.err) && <div style={S.err}>{err || issues.err || returns.err}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.9rem' }}>
        <button type="button" style={S.tab(tab === 'issues')} onClick={() => setTab('issues')}>Issues</button>
        <button type="button" style={S.tab(tab === 'returns')} onClick={() => setTab('returns')}>Returns</button>
        <button type="button" style={S.tab(tab === 'consumption')} onClick={() => setTab('consumption')}>Consumption</button>
      </div>
      {tab === 'issues' && (
        <>
          {canMove && <div style={S.bar}><button type="button" className="btn-primary" onClick={() => { setMsg(''); setIssuing(true); }}>+ Issue items</button></div>}
          <DocList rows={issues.rows} loading={issues.loading} empty="Nothing issued yet." onView={open} columns={[
            colNo, colDate, ['Store', (d) => d.from_store_name], ['Issued to', (d) => <>{d.cost_ref_id}<div style={small}>{label(COST_REF_TYPES, d.cost_ref_type)}</div></>],
            ['Indent', (d) => d.ref_doc_no || '—'], ['Lines', (d) => d.line_count, true], colValue,
          ]} />
        </>
      )}
      {tab === 'returns' && (
        <>
          <p style={{ ...S.sub, marginTop: 0 }}>To return items, open the issue they went out on and press "Return items".</p>
          <DocList rows={returns.rows} loading={returns.loading} empty="No returns yet." onView={open} columns={[
            colNo, colDate, ['Into store', (d) => d.to_store_name], ['From', (d) => d.cost_ref_id || '—'], ['Issue', (d) => d.ref_doc_no || '—'], colValue,
          ]} />
        </>
      )}
      {tab === 'consumption' && <Consumption api={api} boot={boot} />}
      {view && (
        <MoveDocView doc={view} onClose={() => setView(null)}>
          {view.doc_type === 'ISSUE' && canMove && view.lines.some((l) => n(l.qty) - n(l.returned_qty) > 0.000001) && (
            <button type="button" className="btn-primary" onClick={() => { setReturning(view); setView(null); }}>Return items</button>
          )}
        </MoveDocView>
      )}
      {returning && (
        <ReturnForm api={api} issue={returning} onCancel={() => setReturning(null)}
          onDone={(d) => { setReturning(null); setMsg(`Return ${d.doc_no} posted — ${fmtMoney(d.grand_total)} back in stock.`); setTab('returns'); returns.load(); }} />
      )}
    </>
  );
}

function Consumption({ api, boot }) {
  const first = `${today().slice(0, 8)}01`;
  const [f, setF] = useState({ from: first, to: today(), store_id: '', group: 'item' });
  const path = `/reports/consumption?${new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v)))}`;
  const [data, setData] = useState({ rows: [] });
  const [err, setErr] = useState('');
  useEffect(() => { setErr(''); api(path).then(setData).catch((e) => setErr(e.message)); }, [api, path]);
  const total = data.rows.reduce((s, r) => s + n(r.net_value), 0);
  return (
    <>
      <div style={S.bar}>
        <Field label="From"><input type="date" style={S.inp} value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} /></Field>
        <Field label="To"><input type="date" style={S.inp} value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></Field>
        <Field label="Store">
          <Select style={{ ...S.inp, minWidth: 170 }} value={f.store_id} onChange={(e) => setF({ ...f, store_id: e.target.value })}>
            <option value="">All stores</option>
            {boot.stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </Select>
        </Field>
        <Field label="Group by">
          <Select style={S.inp} value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })}>
            <option value="item">Item</option>
            <option value="cost_ref">Issued to</option>
          </Select>
        </Field>
        <strong style={{ paddingBottom: 6, fontVariantNumeric: 'tabular-nums' }}>Net consumption {fmtMoney(total)}</strong>
      </div>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          {f.group === 'item' ? (
            <>
              <thead><tr><th style={S.th}>Item</th><th style={{ ...S.th, ...S.num }}>Issued</th><th style={{ ...S.th, ...S.num }}>Returned</th><th style={{ ...S.th, ...S.num }}>Net qty</th><th style={{ ...S.th, ...S.num }}>Net value</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.item_id}>
                    <td style={S.td}>{r.item_name}<div style={small}>{r.item_code}{r.category_name ? ` · ${r.category_name}` : ''}</div></td>
                    <td style={{ ...S.td, ...S.num }}>{fmtQty(r.issued_qty)} {r.base_uom}</td>
                    <td style={{ ...S.td, ...S.num }}>{n(r.returned_qty) ? fmtQty(r.returned_qty) : '—'}</td>
                    <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtQty(r.net_qty)}</td>
                    <td style={{ ...S.td, ...S.num }}>{fmtMoney(r.net_value)}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td colSpan={5} style={S.empty}>Nothing issued in this period.</td></tr>}
              </tbody>
            </>
          ) : (
            <>
              <thead><tr><th style={S.th}>Issued to</th><th style={S.th}>Type</th><th style={{ ...S.th, ...S.num }}>Items</th><th style={{ ...S.th, ...S.num }}>Net value</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={`${r.cost_ref_type}-${r.cost_ref_id}`}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{r.cost_ref_id}</td><td style={S.td}>{label(COST_REF_TYPES, r.cost_ref_type)}</td>
                    <td style={{ ...S.td, ...S.num }}>{r.items}</td><td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtMoney(r.net_value)}</td>
                  </tr>
                ))}
                {!data.rows.length && <tr><td colSpan={4} style={S.empty}>Nothing issued in this period.</td></tr>}
              </tbody>
            </>
          )}
        </table>
      </div>
    </>
  );
}

// ── Transfers ────────────────────────────────────────────────────────────
export function Transfers({ api, boot }) {
  const { items, byId } = useItems(api);
  const { rows, err: listErr, loading, load } = useList(api, '/docs?doc_type=TRANSFER');
  const stores = boot.stores.filter((s) => s.is_active);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const from = form && boot.stores.find((s) => s.id === form.from_store_id);
  const toStores = from ? stores.filter((s) => s.department_id === from.department_id && s.id !== from.id) : [];
  const [onHand] = useOnHand(api, form?.from_store_id);
  const canMove = can(boot, 'STOREKEEPER', 'STORE_MANAGER');

  const post = async () => {
    setBusy(true); setErr('');
    try {
      const lines = form.lines.filter((l) => l.item_id && n(l.qty) > 0);
      const d = await postWithOverride(api, '/transfers', { ...form, lines }, boot);
      setMsg(`Transfer ${d.doc_no} posted — ${fmtMoney(d.grand_total)} moved.`); setForm(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      {(err || listErr) && !form && <div style={S.err}>{err || listErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {!form && canMove && <div style={S.bar}><button type="button" className="btn-primary" onClick={() => { setMsg(''); setErr(''); const s0 = stores[0]; setForm({ from_store_id: s0?.id || '', to_store_id: '', doc_date: today(), party_name: '', remarks: '', lines: [blankLine()] }); }}>+ New transfer</button></div>}
      {form && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#1e3a5f' }}>Transfer between stores</h3>
          {err && <div style={S.err}>{err}</div>}
          <div style={S.bar}>
            <Field label="From store">
              <Select style={{ ...S.inp, minWidth: 190 }} value={form.from_store_id} onChange={(e) => setForm({ ...form, from_store_id: e.target.value, to_store_id: '', lines: [blankLine()] })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label="To store">
              <Select style={{ ...S.inp, minWidth: 190 }} value={form.to_store_id} onChange={(e) => setForm({ ...form, to_store_id: e.target.value })}>
                <option value="">{toStores.length ? 'Choose…' : 'No other store in this department'}</option>
                {toStores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label="Date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
            <Field label="Carried by"><input style={{ ...S.inp, width: 150 }} value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} /></Field>
          </div>
          <LineTable form={form} setForm={setForm} items={from ? items.filter((i) => i.department_id === from.department_id) : []} byId={byId} onHand={onHand} newLine={() => blankLine()} />
          <Field label="Remarks" style={{ marginTop: '0.75rem' }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
          <p style={S.sub}>Batches move with their batch number, expiry and cost.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={post} disabled={busy || !form.to_store_id}>{busy ? 'Posting…' : 'Post transfer'}</button>
          </div>
        </div>
      )}
      <DocList rows={rows} loading={loading} empty="No transfers yet." onView={(id) => api(`/docs/${id}`).then(setView).catch((e) => setErr(e.message))} columns={[
        colNo, colDate, ['From', (d) => d.from_store_name], ['To', (d) => d.to_store_name], ['Lines', (d) => d.line_count, true], colValue, ['By', (d) => d.posted_by],
      ]} />
      {view && <MoveDocView doc={view} onClose={() => setView(null)} />}
    </>
  );
}

// ── Wastage & adjustments ────────────────────────────────────────────────
export function WastageAdjust({ api, boot }) {
  const { items, byId } = useItems(api);
  const [tab, setTab] = useState('wastage');
  const docType = tab === 'wastage' ? 'WASTAGE' : 'ADJUST';
  const { rows, err: listErr, loading, load } = useList(api, `/docs?doc_type=${docType}`);
  const stores = boot.stores.filter((s) => s.is_active);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const store = form && boot.stores.find((s) => s.id === form.store_id);
  const [onHand] = useOnHand(api, form?.store_id);
  const canWaste = can(boot, 'STOREKEEPER', 'STORE_MANAGER');
  const canAdjust = can(boot, 'STORE_MANAGER');
  const isWaste = tab === 'wastage';
  const newLine = () => blankLine(isWaste ? { reason: 'EXPIRED' } : { direction: 'OUT', reason: 'MISSING', rate: '' });

  const post = async () => {
    setBusy(true); setErr('');
    try {
      const lines = form.lines.filter((l) => l.item_id && n(l.qty) > 0);
      if (!lines.length) throw new Error('Add at least one item with a quantity');
      const d = await api(isWaste ? '/wastage' : '/adjustments', { method: 'POST', body: { ...form, lines } });
      setMsg(`${d.doc_no} posted — ${fmtMoney(d.grand_total)}.`); setForm(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      {(err || listErr) && !form && <div style={S.err}>{err || listErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.9rem' }}>
        <button type="button" style={S.tab(isWaste)} onClick={() => { setTab('wastage'); setForm(null); }}>Wastage</button>
        <button type="button" style={S.tab(!isWaste)} onClick={() => { setTab('adjust'); setForm(null); }}>Adjustments</button>
      </div>
      {!form && (isWaste ? canWaste : canAdjust) && (
        <div style={S.bar}>
          <button type="button" className="btn-primary" onClick={() => { setMsg(''); setErr(''); setForm({ store_id: stores[0]?.id || '', doc_date: today(), remarks: '', lines: [newLine()] }); }}>{isWaste ? '+ Record wastage' : '+ New adjustment'}</button>
        </div>
      )}
      {!isWaste && !canAdjust && <p style={{ ...S.sub, marginTop: 0 }}>Only the Store Manager can post adjustments.</p>}
      {form && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#1e3a5f' }}>{isWaste ? 'Record wastage' : 'Stock adjustment'}</h3>
          {err && <div style={S.err}>{err}</div>}
          <div style={S.bar}>
            <Field label="Store">
              <Select style={{ ...S.inp, minWidth: 190 }} value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value, lines: [newLine()] })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label="Date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
          </div>
          <LineTable form={form} setForm={setForm} items={store ? items.filter((i) => i.department_id === store.department_id) : []} byId={byId} onHand={onHand} newLine={newLine}
            extraHead={isWaste ? <th style={S.th}>Reason</th> : <><th style={S.th}>+ / −</th><th style={S.th}>Reason</th><th style={{ ...S.th, ...S.num }}>Rate (for +)</th></>}
            extraCells={(l, it, set) => (isWaste ? (
              <td style={S.td}><Select style={S.inp} value={l.reason} onChange={(e) => set({ reason: e.target.value })}>{WASTAGE_REASONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></td>
            ) : (
              <>
                <td style={S.td}>
                  <Select style={{ ...S.inp, fontWeight: 700, color: l.direction === 'IN' ? '#15803d' : '#b91c1c' }} value={l.direction} onChange={(e) => set({ direction: e.target.value })}>
                    <option value="OUT">− Remove</option><option value="IN">+ Add</option>
                  </Select>
                </td>
                <td style={S.td}><Select style={S.inp} value={l.reason} onChange={(e) => set({ reason: e.target.value })}>{ADJUST_REASONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></td>
                <td style={S.td}>{l.direction === 'IN' ? <input type="number" min="0" step="any" style={{ ...S.inp, width: 95, textAlign: 'right' }} value={l.rate} placeholder="avg cost" onChange={(e) => set({ rate: e.target.value })} /> : <span style={small}>at cost</span>}</td>
              </>
            ))} />
          <Field label={isWaste ? 'Remarks' : 'Why is this adjustment needed? *'} style={{ marginTop: '0.75rem' }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={post} disabled={busy || (!isWaste && form.remarks.trim().length < 5)}>{busy ? 'Posting…' : isWaste ? 'Post wastage' : 'Post adjustment'}</button>
          </div>
        </div>
      )}
      <DocList rows={rows} loading={loading} empty={isWaste ? 'No wastage recorded.' : 'No adjustments.'} onView={(id) => api(`/docs/${id}`).then(setView).catch((e) => setErr(e.message))} columns={[
        colNo, colDate, ['Store', (d) => d.from_store_name], ['Lines', (d) => d.line_count, true], colValue, ['By', (d) => d.posted_by], ['Remarks', (d) => d.remarks || '—'],
      ]} />
      {view && <MoveDocView doc={view} onClose={() => setView(null)} />}
    </>
  );
}

// ── Physical count ───────────────────────────────────────────────────────
export function PhysicalCount({ api, boot }) {
  const { rows, err: listErr, loading, load } = useList(api, '/docs?doc_type=COUNT');
  const stores = boot.stores.filter((s) => s.is_active);
  const [start, setStart] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [view, setView] = useState(null);
  const [q, setQ] = useState('');
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const canCount = can(boot, 'STOREKEEPER', 'STORE_MANAGER', 'COST_CONTROLLER');
  const canPost = can(boot, 'STORE_MANAGER');

  const openDoc = async (id) => {
    setErr('');
    try {
      const d = await api(`/docs/${id}`);
      if (d.status === 'DRAFT') setSheet({ ...d, lines: d.lines.map((l) => ({ ...l, counted: l.counted_qty != null ? String(n(l.counted_qty)) : '' })) });
      else setView(d);
    } catch (e) { setErr(e.message); }
  };
  const begin = async () => {
    setBusy(true); setErr('');
    try {
      const d = await api('/counts', { method: 'POST', body: start });
      setStart(null); load(); await openDoc(d.id);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const save = async (quiet) => {
    await api(`/counts/${sheet.id}`, { method: 'PUT', body: { lines: sheet.lines.map((l) => ({ line_id: l.id, counted_qty: l.counted, remarks: l.remarks })) } });
    if (!quiet) setMsg(`${sheet.doc_no} saved.`);
  };
  const run = async (fn) => {
    setBusy(true); setErr(''); setMsg('');
    try { await fn(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const postCount = () => run(async () => {
    const counted = sheet.lines.filter((l) => l.counted !== '').length;
    if (!window.confirm(`Post ${sheet.doc_no}? ${counted} counted item(s) will be set to the counted quantity; the difference is posted as gain / loss. Items left blank are not changed.`)) return;
    await save(true);
    const d = await api(`/counts/${sheet.id}/post`, { method: 'POST', body: {} });
    setSheet(null); setMsg(`${d.doc_no} posted — net ${fmtMoney(d.grand_total)}.`); load();
  });
  const cancelCount = () => run(async () => {
    if (!window.confirm(`Cancel ${sheet.doc_no}? Counted quantities are discarded.`)) return;
    await api(`/counts/${sheet.id}/cancel`, { method: 'POST', body: { reason: 'Cancelled' } });
    setSheet(null); setMsg('Count cancelled.'); load();
  });

  if (sheet) {
    const shown = sheet.lines.filter((l) => (!q.trim() || `${l.item_name} ${l.item_code}`.toLowerCase().includes(q.trim().toLowerCase()))
      && (!onlyDiff || (l.counted !== '' && Math.abs(n(l.counted) - n(l.system_qty)) > 0.0001)));
    const counted = sheet.lines.filter((l) => l.counted !== '');
    const net = counted.reduce((s, l) => s + (n(l.counted) - n(l.system_qty)) * n(l.base_rate), 0);
    const setCounted = (id, v) => setSheet((s) => ({ ...s, lines: s.lines.map((l) => (l.id === id ? { ...l, counted: v } : l)) }));
    return (
      <>
        {err && <div style={S.err}>{err}</div>}
        {msg && <div style={S.ok}>{msg}</div>}
        <div style={{ ...S.card, display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <strong>{sheet.doc_no}</strong> · {sheet.from_store_name} · {fmtDate(sheet.doc_date)}
            <div style={S.sub}>{counted.length} of {sheet.lines.length} items counted · estimated net {fmtMoney(net)}</div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button type="button" className="btn-muted" onClick={() => setSheet(null)} disabled={busy}>Back</button>
            <button type="button" className="btn-muted" onClick={() => window.print()}>Print sheet</button>
            {canPost && <button type="button" className="btn-muted" onClick={cancelCount} disabled={busy}>Cancel count</button>}
            <button type="button" className="btn-muted" onClick={() => run(() => save(false))} disabled={busy}>{busy ? 'Saving…' : 'Save progress'}</button>
            {canPost && <button type="button" className="btn-primary" onClick={postCount} disabled={busy || !counted.length}>Post count</button>}
          </div>
        </div>
        <div style={S.bar}>
          <Field label="Find item"><input style={{ ...S.inp, width: 230 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or code" /></Field>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem', paddingBottom: 6 }}><input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} /> Only differences</label>
        </div>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Item</th><th style={{ ...S.th, ...S.num }}>System qty</th><th style={{ ...S.th, ...S.num }}>Counted</th>
              <th style={{ ...S.th, ...S.num }}>Difference</th><th style={{ ...S.th, ...S.num }}>Value</th>
            </tr></thead>
            <tbody>
              {shown.map((l) => {
                const diff = l.counted === '' ? null : r4(n(l.counted) - n(l.system_qty));
                return (
                  <tr key={l.id}>
                    <td style={S.td}>{l.item_name}<div style={small}>{l.item_code}</div></td>
                    <td style={{ ...S.td, ...S.num }}>{fmtQty(l.system_qty)} {l.uom_code}</td>
                    <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 100, textAlign: 'right', marginLeft: 'auto', display: 'block' }} value={l.counted} onChange={(e) => setCounted(l.id, e.target.value)} /></td>
                    <td style={{ ...S.td, ...S.num, fontWeight: 700, color: diff == null || Math.abs(diff) < 0.0001 ? undefined : diff < 0 ? '#b91c1c' : '#15803d' }}>{diff == null ? '' : `${diff > 0 ? '+' : ''}${fmtQty(diff)}`}</td>
                    <td style={{ ...S.td, ...S.num }}>{diff == null ? '' : fmtMoney(diff * n(l.base_rate))}</td>
                  </tr>
                );
              })}
              {!shown.length && <tr><td colSpan={5} style={S.empty}>No items match.</td></tr>}
            </tbody>
          </table>
        </div>
        <p style={S.sub}>Quantities are in each item's base unit. When posted, the difference is worked out against the stock at that moment, so movements made during the count are taken into account.</p>
      </>
    );
  }

  return (
    <>
      {(err || listErr) && <div style={S.err}>{err || listErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {canCount && !start && <div style={S.bar}><button type="button" className="btn-primary" onClick={() => { setMsg(''); setStart({ store_id: stores[0]?.id || '', doc_date: today(), category_id: '', only_in_stock: false, remarks: '' }); }}>+ Start a count</button></div>}
      {start && (
        <div style={S.card}>
          <div style={S.bar}>
            <Field label="Store">
              <Select style={{ ...S.inp, minWidth: 190 }} value={start.store_id} onChange={(e) => setStart({ ...start, store_id: e.target.value, category_id: '' })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label="Category (optional)">
              <Select style={{ ...S.inp, minWidth: 180 }} value={start.category_id} onChange={(e) => setStart({ ...start, category_id: e.target.value })}>
                <option value="">All categories</option>
                {boot.categories.filter((c) => c.department_id === boot.stores.find((s) => s.id === start.store_id)?.department_id).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Count date"><input type="date" style={S.inp} value={start.doc_date} onChange={(e) => setStart({ ...start, doc_date: e.target.value })} /></Field>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem', paddingBottom: 6 }}><input type="checkbox" checked={start.only_in_stock} onChange={(e) => setStart({ ...start, only_in_stock: e.target.checked })} /> Only items with stock</label>
            <button type="button" className="btn-muted" onClick={() => setStart(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={begin} disabled={busy || !start.store_id}>{busy ? 'Starting…' : 'Start count'}</button>
          </div>
        </div>
      )}
      <DocList rows={rows} loading={loading} empty="No counts yet." onView={openDoc} columns={[
        colNo, colDate, ['Store', (d) => d.from_store_name], ['Items', (d) => d.line_count, true],
        ['Net gain / loss', (d) => (d.status === 'POSTED' ? <span style={{ color: n(d.grand_total) < 0 ? '#b91c1c' : '#15803d' }}>{fmtMoney(d.grand_total)}</span> : '—'), true], colStatus,
      ]} />
      {view && <MoveDocView doc={view} onClose={() => setView(null)} />}
    </>
  );
}
